import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import {
  Box,
  CardActionArea,
  Chip,
  Modal,
  Typography,
  Tooltip,
  Stack,
  Avatar,
} from '@mui/material';
import { IDeviceProps } from 'services/OnPremiseManagementStatusService/types';
import ComputerIcon from '@mui/icons-material/Computer';
import { getLicenseByIdAsync } from 'services/LicenseService';
import { ILicenseSimpleProps } from 'services/LicenseService/types';
import StorageIcon from '@mui/icons-material/Storage';
import CardDeviceServer from './_ui/CardDeviceServer';
import { Devices } from '@mui/icons-material';
import IsOnlineComponent from '../../../components/IsOnlineComponent';

interface CardServerProps {
  device: IDeviceProps;
  allDevices: IDeviceProps[];
  isOnlineApi: boolean;
  isOnlineDeviceServer: boolean;
  onPremiseLicenseId: string;
  onPremiseManagementStatusId: number;
}

const CardServer = ({
  device,
  allDevices,
  isOnlineApi,
  isOnlineDeviceServer,
  onPremiseLicenseId,
  onPremiseManagementStatusId,
}: CardServerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [license, setLicense] = useState<ILicenseSimpleProps | undefined>(
    undefined,
  );

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  useEffect(() => {
    getLicenseByIdAsync(onPremiseLicenseId)
      .then((license) => {
        setLicense(license);
      })
      .catch((error) => {
        console.error('Error fetching license:', error);
      });
  }, [onPremiseLicenseId]);

  return (
    <>
      <CardActionArea onClick={handleOpenModal}>
        <Card
          sx={{
            width: '100%',
            minHeight: 250,
            p: 3,
            backgroundColor: '#202940',
            borderRadius: 5,
            boxShadow: '0 6px 32px 0 rgba(0,0,0,0.25)',
            transition: 'transform 0.15s, box-shadow 0.15s',
            '&:hover': {
              transform: 'translateY(-4px) scale(1.02)',
              boxShadow: '0 12px 40px 0 rgba(0,0,0,0.35)',
            },
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  bgcolor: isOnlineDeviceServer ? '#43e97b' : '#e94343',
                  color: '#fff',
                  width: 48,
                  height: 48,
                  boxShadow: 2,
                }}
              >
                <ComputerIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: 1,
                    mb: 0.5,
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {license?.name}
                </Typography>
                <Chip
                  label={`Plano: ${license?.plans}`}
                  sx={{
                    bgcolor: '#293556',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                  size="small"
                />
              </Box>
            </Stack>
            <Stack spacing={1} alignItems="flex-end">
              <Chip
                icon={<Devices />}
                color={isOnlineDeviceServer ? 'success' : 'error'}
                label={isOnlineDeviceServer ? 'Online' : 'Offline'}
                sx={{
                  fontWeight: 600,
                  fontSize: 15,
                  px: 1.5,
                  bgcolor: isOnlineDeviceServer ? '#43e97b' : '#e94343',
                  color: '#fff',
                }}
              />
              <Chip
                icon={<StorageIcon />}
                label={isOnlineApi ? 'API On' : 'API Off'}
                color={isOnlineApi ? 'success' : 'error'}
                size="small"
                sx={{
                  bgcolor: isOnlineApi ? '#43e97b' : '#e94343',
                  color: '#fff',
                  fontWeight: 500,
                  fontSize: 14,
                }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} mt={2}>
            {allDevices.map((device, index) => (
              <Tooltip
                key={index}
                title={`Dispositivo ${device.deviceName} ${device.isOnline ? 'online' : 'offline'}`}
                arrow
                placement="top"
              >
                <Box>
                  <IsOnlineComponent
                    isOnline={device.isOnline}
                    width={22}
                    height={22}
                  />
                </Box>
              </Tooltip>
            ))}
          </Stack>
        </Card>
      </CardActionArea>
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            bgcolor: '#202940',
            p: { xs: 1.5, sm: 3 },
            borderRadius: 4,
            width: { xs: '98vw', sm: '80vw', md: '60vw', lg: '50vw' },
            maxWidth: '100vw',
            maxHeight: { xs: '95vh', sm: '90vh' },
            boxShadow: 24,
            position: 'relative',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 24,
              zIndex: 1,
            }}
            onClick={handleCloseModal}
            aria-label="Fechar"
          >
            &#10005;
          </Box>
          <CardDeviceServer
            onPremiseManagementStatusId={onPremiseManagementStatusId}
            devices={allDevices}
            license={license}
            isOnlineApi={isOnlineApi}
            isOnlineDeviceServer={isOnlineDeviceServer}
          />
        </Box>
      </Modal>
    </>
  );
};

export default CardServer;
